import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Dimension { id: number; key: string; label: string; kind: string; unit: string | null; }
interface ProviderLink {
  provider_id: number;
  provider_type: string;
  provider_mode: string;
  provider_label: string;
  provider_is_active: boolean;
  provider_product_id: string | null;
  provider_price_id: string | null;
  synced_at: string | null;
  last_sync_error: string | null;
}
interface Product {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'archived';
  billing_interval: 'month' | 'year' | 'one_time';
  price_cents: number;
  currency: string;
  trial_days: number;
  limits: Array<{ dimension_id: number; value: number | null }>;
  provider_links: ProviderLink[];
}

export function Products() {
  const [products, setProducts] = useState<Product[] | null>(null);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [editing, setEditing] = useState<'new' | Product | null>(null);

  async function load() {
    setError(null);
    try {
      const [p, d] = await Promise.all([
        api<{ products: Product[] }>('/admin/products'),
        api<{ dimensions: Dimension[] }>('/admin/products/dimensions'),
      ]);
      setProducts(p.products);
      setDimensions(d.dimensions);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => { load(); }, []);

  function flash(msg: string, isError = false) {
    if (isError) {
      setError(msg);
      setTimeout(() => setError(null), 6000);
    } else {
      setInfo(msg);
      setTimeout(() => setInfo(null), 4000);
    }
  }

  async function sync(productId: number) {
    try {
      const r = await api<{ provider_price_id: string }>(`/admin/products/${productId}/sync`, { method: 'POST' });
      flash(`Synced — price id ${r.provider_price_id}`);
      load();
    } catch (e) {
      flash((e as Error).message, true);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ flex: 1 }}>Products</h1>
        <button onClick={() => setEditing('new')}>+ New product</button>
      </div>
      <p className="subtitle">
        Define what you sell on the signup page. Each product can be priced once and resolved
        to per-provider Stripe IDs automatically — switch sandbox ↔ live without re-publishing.
      </p>
      {error && <div className="banner err">{error}</div>}
      {info && <div className="banner ok">{info}</div>}

      {editing && (
        <ProductEditor
          initial={editing === 'new' ? undefined : editing}
          dimensions={dimensions}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); flash('Saved'); }}
          onError={(m) => flash(m, true)}
        />
      )}

      {!products ? (
        <div className="muted">Loading…</div>
      ) : products.length === 0 ? (
        <div className="card muted">No products yet — create one to start selling.</div>
      ) : (
        products.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            dimensions={dimensions}
            onEdit={() => setEditing(p)}
            onSync={() => sync(p.id)}
          />
        ))
      )}
    </div>
  );
}

function ProductCard({
  product, dimensions, onEdit, onSync,
}: {
  product: Product;
  dimensions: Dimension[];
  onEdit: () => void;
  onSync: () => void;
}) {
  const priceFmt = (product.price_cents / 100).toLocaleString('en-CA', {
    style: 'currency', currency: product.currency.toUpperCase(),
  });
  const intervalFmt = product.billing_interval === 'month' ? '/mo' : product.billing_interval === 'year' ? '/yr' : '';
  const statusPill =
    product.status === 'active' ? 'ok' :
    product.status === 'draft' ? 'warn' : 'muted';

  const activeLink = product.provider_links.find((l) => l.provider_is_active);

  return (
    <div className="card">
      <div className="card-header">
        <h3>
          {product.name} <span className="muted small">({product.slug})</span>
        </h3>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span className={`pill ${statusPill}`}>{product.status}</span>
          <span className="muted">{priceFmt}<span className="small">{intervalFmt}</span></span>
          {product.trial_days > 0 && <span className="pill muted">{product.trial_days}d trial</span>}
        </div>
      </div>
      {product.description && (
        <p className="muted small" style={{ margin: '4px 0 12px' }}>{product.description}</p>
      )}

      {product.limits.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginBottom: 12 }}>
          {product.limits.map((l) => {
            const dim = dimensions.find((d) => d.id === l.dimension_id);
            if (!dim) return null;
            const valueFmt = dim.kind === 'boolean' ? (l.value ? '✓ on' : '✗ off')
              : dim.kind === 'bytes' && l.value != null ? `${(l.value / (1024**3)).toFixed(1)} GiB`
              : dim.kind === 'currency_cents' && l.value != null ? `$${(l.value/100).toLocaleString()}`
              : l.value == null ? 'unlimited' : l.value.toLocaleString();
            return (
              <div key={l.dimension_id} className="small">
                <div className="muted">{dim.label}</div>
                <div style={{ fontWeight: 500 }}>{valueFmt}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="small" style={{ marginTop: 8 }}>
        <strong>Provider sync:</strong>{' '}
        {product.provider_links.length === 0 ? (
          <span className="muted">not yet synced to any provider</span>
        ) : (
          product.provider_links.map((l) => (
            <span key={l.provider_id} style={{ marginRight: 10 }}>
              <span className={`pill ${l.last_sync_error ? 'err' : 'ok'}`}>
                {l.provider_label}
              </span>{' '}
              {l.provider_price_id && <code className="code">{l.provider_price_id}</code>}
              {l.last_sync_error && (
                <span className="error-text"> {l.last_sync_error}</span>
              )}
            </span>
          ))
        )}
        {!activeLink && (
          <div className="error-text" style={{ marginTop: 4 }}>
            ⚠ Not synced to the currently active provider — signups for this product will fail.
            Click "Sync to active provider" below.
          </div>
        )}
      </div>

      <div className="button-row">
        <button className="secondary" onClick={onEdit}>Edit</button>
        <button onClick={onSync}>Sync to active provider</button>
      </div>
    </div>
  );
}

function ProductEditor({
  initial, dimensions, onClose, onSaved, onError,
}: {
  initial?: Product;
  dimensions: Dimension[];
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [status, setStatus] = useState<Product['status']>(initial?.status ?? 'draft');
  const [billingInterval, setBillingInterval] = useState<Product['billing_interval']>(initial?.billing_interval ?? 'month');
  const [priceDollars, setPriceDollars] = useState(initial ? String(initial.price_cents / 100) : '');
  const [currency, setCurrency] = useState(initial?.currency ?? 'cad');
  const [trialDays, setTrialDays] = useState(String(initial?.trial_days ?? 14));
  const [limits, setLimits] = useState<Record<number, string>>(() => {
    const m: Record<number, string> = {};
    if (initial) initial.limits.forEach((l) => { m[l.dimension_id] = l.value == null ? '' : String(l.value); });
    return m;
  });
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const body = {
      slug, name,
      description: description || null,
      status,
      billing_interval: billingInterval,
      price_cents: Math.round(Number(priceDollars) * 100),
      currency: currency.toLowerCase(),
      trial_days: Number(trialDays),
      limits: dimensions.map((d) => ({
        dimension_id: d.id,
        value: limits[d.id] === '' || limits[d.id] === undefined ? null : Number(limits[d.id]),
      })),
    };
    try {
      if (initial) {
        await api(`/admin/products/${initial.id}`, { method: 'PUT', json: body });
      } else {
        await api('/admin/products', { method: 'POST', json: body });
      }
      onSaved();
    } catch (e) {
      onError((e as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div className="card" style={{ borderColor: 'var(--accent)' }}>
      <div className="card-header">
        <h3>{initial ? `Edit ${initial.slug}` : 'New product'}</h3>
        <button className="secondary" onClick={onClose}>Cancel</button>
      </div>
      <form onSubmit={submit}>
        <div className="form-row">
          <div>
            <label>Slug</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)} required pattern="[a-z0-9-]{1,32}" disabled={!!initial} />
            <div className="helper">Lowercase, dashes only. URL-safe.</div>
          </div>
          <div>
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
        </div>
        <label>Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />

        <div className="form-row">
          <div>
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value as Product['status'])}>
              <option value="draft">draft (not for sale)</option>
              <option value="active">active (for sale)</option>
              <option value="archived">archived</option>
            </select>
          </div>
          <div>
            <label>Billing interval</label>
            <select value={billingInterval} onChange={(e) => setBillingInterval(e.target.value as Product['billing_interval'])}>
              <option value="month">monthly</option>
              <option value="year">yearly</option>
              <option value="one_time">one-time</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div>
            <label>Price</label>
            <input type="number" step="0.01" min="0" value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)} required />
          </div>
          <div>
            <label>Currency (ISO-3)</label>
            <input value={currency} onChange={(e) => setCurrency(e.target.value)} required maxLength={3} />
          </div>
        </div>

        <div className="form-row">
          <div>
            <label>Trial days</label>
            <input type="number" min="0" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} required />
            <div className="helper">0 disables the trial.</div>
          </div>
          <div />
        </div>

        <h2 style={{ marginTop: 24, marginBottom: 8 }}>Limits</h2>
        <p className="muted small" style={{ marginBottom: 12 }}>
          Per-dimension caps. Leave blank for unlimited; for boolean dimensions, 1 = on, 0 = off.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {dimensions.map((d) => (
            <div key={d.id}>
              <label style={{ textTransform: 'none' }}>
                {d.label}
                <span className="muted small" style={{ marginLeft: 4, fontWeight: 400 }}>({d.kind}{d.unit ? `, ${d.unit}` : ''})</span>
              </label>
              <input
                type="number"
                value={limits[d.id] ?? ''}
                onChange={(e) => setLimits({ ...limits, [d.id]: e.target.value })}
                placeholder={d.kind === 'boolean' ? '0 or 1' : 'blank = unlimited'}
              />
            </div>
          ))}
        </div>

        <div className="button-row">
          <button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Save product'}</button>
          <button type="button" className="secondary" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
